/** GM water edits use the same contact, clock and environmental identity as movement. */
import { encounterField, encounterCreatureField, layerOf, layerEntriesOf, posCoord, sizeIdOf } from '../../shared/array-types.js';
import { CREATURE_SIZE_TYPES } from '../../shared/creature-size-types.js';
import { physicalCreatureBody } from '../../shared/physical-creature-body.js';
import { meshesOf } from '../../../../ft3d/engine/mesh-presentation.js';
import { ftClockCalendarFromArr, ftClockBaseFromArr, ftDeriveCampaignClock, type CampaignClockCols } from '../../shared/campaign-clock.js';
import { statusRows } from '../statuses/expiry.js';
import { encounterWaterModel } from './traversal-3d.js';
import { resolveWaterWet, waterContactDepth, projectWaterWet, waterWetClock, type WaterWetChange } from './water-wet.js';
import type { DifficultTerrainReaders } from './difficult-terrain.js';

type Row = Record<string, unknown>;
const record = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
export interface WaterLevelChange {
  kind: 'water_wet'; actor_id: string;
  position: { x_pos: number; y_pos: number; z_pos: number };
  wet: WaterWetChange;
}
export interface WaterLevelPlan {
  expected_level_ft: number | null; level_ft: number | null; wet_changes: WaterLevelChange[];
}
export interface WaterLevelReaders extends DifficultTerrainReaders {
  position: (row: Row) => readonly [number, number, number];
  height: (row: Row) => number | null;
}

/** Stateless equivalents of the page movement readers; no cross-bundle module state. */
export function waterLevelReaders(encounter: Row): WaterLevelReaders {
  return {
    value: layerOf,
    entries: (row, name) => {
      const compiled = record(record(record(row.computed)?.creature)?.abilities)?.[name];
      return Array.isArray(compiled) ? compiled : layerEntriesOf(row, name);
    },
    definition: id => record(record(encounter.catalog)?.[id.toUpperCase()]),
    statuses: row => statusRows(row, 'target'),
    position: row => [posCoord(row, 'x_pos') ?? 0, posCoord(row, 'y_pos') ?? 0, posCoord(row, 'z_pos') ?? 0],
    height: row => physicalCreatureBody(meshesOf({ ...row, catalog: row.catalog ?? encounter.catalog } as never))?.height
      ?? CREATURE_SIZE_TYPES.find(size => size.item_id === String(sizeIdOf(row) ?? '').toUpperCase())?.base_size ?? null,
  };
}

export function planWaterLevel(input: {
  encounter: Row; expectedLevelFt: number | null; levelFt: number | null;
  /** Non-campaign authoring has no timed statuses. Campaign play always supplies its clock. */
  campaign: Row | null; now?: CampaignClockCols; newId: () => string; read?: WaterLevelReaders;
}): WaterLevelPlan {
  const { encounter, expectedLevelFt, levelFt, campaign } = input;
  for (const level of [expectedLevelFt, levelFt]) if (level !== null
    && (!Number.isInteger(level) || level < -2147483648 || level > 2147483647)) throw new Error('water_level_requires_whole_feet');
  const plan: WaterLevelPlan = { expected_level_ft: expectedLevelFt, level_ft: levelFt, wet_changes: [] };
  if (expectedLevelFt === levelFt || !campaign) return plan;
  if (!Array.isArray(encounter.encounter_creatures_arr)) throw new Error('Water roster is still loading.');
  const calendar = ftClockCalendarFromArr(campaign), now = input.now ?? ftDeriveCampaignClock(ftClockBaseFromArr(campaign), Date.now()).cols;
  if (!calendar.months.length) throw new Error('Campaign calendar is still loading.');
  const scene = (level: number | null): Row => ({ ...encounter, encounters: {
    ...record(encounter.encounters), encounter_shape: encounterField(encounter, 'encounter_shape'), encounter_water_level: level,
  }, encounter_water_level: level });
  const before = scene(expectedLevelFt), after = scene(levelFt), read = input.read ?? waterLevelReaders(encounter);
  for (const value of encounter.encounter_creatures_arr) {
    const actor = record(value);
    if (!actor) throw new Error('Water roster contains an unavailable creature.');
    const actorId = String(encounterCreatureField(actor.encounter_creatures, 'encounter_creature_id') ?? '').toUpperCase();
    const point = read.position(actor), height = read.height(actor);
    if (!actorId || !point.every(Number.isFinite)) throw new Error('Water creature position is unavailable.');
    // A dry actor needs no body measurement. Contact uses the actual physical height once present.
    const oldDepth = waterContactDepth(encounterWaterModel(before), ...point, height ?? 1);
    const newDepth = waterContactDepth(encounterWaterModel(after), ...point, height ?? 1);
    if (Math.max(oldDepth, newDepth) <= 0) continue;
    if (!(height && height > 0)) throw new Error('Water creature body height is unavailable.');
    const wet = resolveWaterWet({ encounter: oldDepth > newDepth ? before : after, actor, actorId,
      route: [point], height, now, calendar, movementReaders: read, newId: input.newId });
    if (!wet) continue;
    // Only the fixed storage columns cross the save boundary; sparse clocks normalize in PHP.
    const prior = wet.before;
    if (prior) {
      const clocks = Object.fromEntries((['status_starts_campaign', 'status_ends_campaign'] as const).flatMap(prefix =>
        Object.entries(waterWetClock(prior, prefix)).map(([unit, value]) => [`${prefix}_${unit}`, value])));
      wet.before = { status_id: prior.status_id, ...clocks };
    }
    plan.wet_changes.push({ kind: 'water_wet', actor_id: actorId,
      position: { x_pos: point[0], y_pos: point[1], z_pos: point[2] }, wet });
  }
  return plan;
}

/** Install confirmed raw rows in the held scene/parent snapshots, retaining catalog hydration. */
export function applyWaterLevelReadback(encounter: Row, level: number | null, rows: Array<{ actor_id: string; after: Row }>): void {
  const bucket = record(encounter.encounters);
  if (bucket) bucket.encounter_water_level = level;
  else encounter.encounter_water_level = level;
  const roster = Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr : [];
  for (const change of rows) {
    const actor = roster.map(record).find(row => row
      && String(encounterCreatureField(row.encounter_creatures, 'encounter_creature_id') ?? '').toUpperCase() === change.actor_id.toUpperCase());
    if (actor) projectWaterWet(actor, { before: null, after: change.after });
  }
}
